import {useQuery} from '@tanstack/react-query';
import TournamentRestClient from '@/Api/TournamentRestClient';

export interface UpcomingArena {
  id: number;
  name: string;
  start_date: string;
  end_date?: string;
  duration_minutes?: number;
  tournament_type?: string;
  tournamentType?: string;
  [key: string]: any;
}

interface TournamentsListResponse {
  // get-circlechess-tournaments wraps the array under `data` (see useGetTournamentsList).
  data?: UpcomingArena[];
}

const isArena = (t: UpcomingArena): boolean =>
  (t.tournament_type ?? t.tournamentType) === 'Circlechess_Arena';

/** End of the arena, from end_date, else start + duration_minutes; NaN if unknown. */
const arenaEndMs = (t: UpcomingArena, startMs: number): number => {
  if (t.end_date) return new Date(t.end_date).getTime();
  if (t.duration_minutes) return startMs + t.duration_minutes * 60 * 1000;
  return NaN;
};

/**
 * A Circlechess_Arena is surfaceable if it is either upcoming (start in the
 * future) or currently running with at least HALF its duration still left
 * (now <= midpoint). Running arenas past the half-way mark are dropped so the
 * banner rolls over to the next upcoming one.
 */
const isSurfaceable = (t: UpcomingArena, now: number): boolean => {
  if (!isArena(t)) return false;
  const startMs = new Date(t.start_date).getTime();
  if (Number.isNaN(startMs)) return false;
  if (startMs > now) return true; // upcoming
  const endMs = arenaEndMs(t, startMs); // running
  if (Number.isNaN(endMs) || now >= endMs) return false;
  const midpointMs = startMs + (endMs - startMs) / 2;
  return now <= midpointMs;
};

/**
 * Pick the arena to promote: a running-first-half arena wins over the next
 * upcoming one (its start is in the past, so it sorts first).
 */
const pickNextArena = (
  tournaments: UpcomingArena[],
): UpcomingArena | null => {
  const now = Date.now();
  const arenas = tournaments
    .filter(t => isSurfaceable(t, now))
    .sort(
      (a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime(),
    );

  return arenas[0] ?? null;
};

const fetchUpcomingArena = async (
  userId: string | number,
): Promise<UpcomingArena | null> => {
  const response = await TournamentRestClient.get(
    `/tournament/get-circlechess-tournaments?limit=20&offset=0&userId=${userId}`,
  );
  const payload: TournamentsListResponse = response.data ?? {};
  const tournaments: UpcomingArena[] = payload.data ?? [];
  return pickNextArena(tournaments);
};

export default function useGetUpcomingArena(userId?: string | number) {
  return useQuery<UpcomingArena | null, Error>(
    ['upcoming-arena', userId],
    () => fetchUpcomingArena(userId as string | number),
    {
      enabled: !!userId,
      staleTime: 5 * 60 * 1000,
      retry: 1,
    },
  );
}
